import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheckIcon as ShieldCheck, ScalesIcon as Scales, ListChecksIcon as ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PublicShell from "@/components/PublicShell";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { RowListSkeleton } from "@/components/SkeletonList";
import { formatApiError } from "@/lib/utils-app";

/**
 * Public Buyer's Bill of Rights landing page.
 *
 * Mirrors `SellerStandards.jsx`: prominent venue framing, 10
 * recommendations grouped as numbered cards, explicit liability
 * disclaimer, plus a one-click "I've read my rights" acknowledgment
 * for signed-in buyers (creates an audit trail used at escrow
 * checkout to skip the inline ack step).
 */
export default function BuyerRights() {
  const { user } = useAuth() || {};
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acked, setAcked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/buyer-rights/current")
      .then((r) => alive && setDoc(r.data))
      .finally(() => alive && setLoading(false));
    if (user) {
      api.get("/buyer-rights/my-acknowledgments")
        .then((r) => alive && setAcked(Boolean(r.data?.has_acknowledged_current)))
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [user]);

  const ack = async () => {
    setSubmitting(true);
    try {
      await api.post("/buyer-rights/acknowledge", { context: "standalone" });
      toast.success("Recorded — thank you. Your acknowledgment is now on file.");
      setAcked(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  const Shell = user ? AppShell : PublicShell;
  const total = useMemo(() => (doc?.rights || []).length, [doc]);
  return (
    <Shell>
      <article className="max-w-3xl mx-auto" data-testid="buyer-rights-page">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-crimson font-bold">
          <Scales size={12} weight="fill" />
          Buyer's bill of rights · {doc?.version || "—"}
        </div>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight text-ink mb-4">
          Your <span className="italic">10</span> rights as a buyer
        </h1>
        <p className="text-[15px] text-[#3F3F3F] leading-relaxed mb-6 max-w-2xl">
          {doc?.preface || "Loading…"}
        </p>

        {doc?.venue_statement && (
          <div
            className="rounded-2xl bg-[#0E0E0E] text-white/85 p-5 mb-6 max-w-2xl"
            data-testid="rights-venue-statement"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-gold mb-2">
              The Registry's role · venue
            </p>
            <p className="text-[13px] leading-relaxed">{doc.venue_statement}</p>
          </div>
        )}

        {!loading && user && (
          <div
            className={`rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 mb-8 ${
              acked ? "border-[#0F8043]/40 bg-[#F1FAF3]" : "border-[#C1272D]/30 bg-[#FFF5F5]"
            }`}
            data-testid="rights-ack-card"
          >
            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              acked ? "bg-[#0F8043]/10 text-[#0F8043]" : "bg-[#C1272D]/10 text-[#C1272D]"
            }`}>
              <ListChecks size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-ink mb-1">
                {acked ? "You've read your rights ✓" : "Sign your acknowledgment"}
              </h2>
              <p className="text-[13px] text-[#3F3F3F] leading-relaxed">
                {acked
                  ? "Your acknowledgment of the current version is on file. You'll skip the inline ack step at marketplace checkout."
                  : "One-click acknowledgment that you've read these rights. Recorded with timestamp + version. Required at escrow checkout — do it now and skip it later."}
              </p>
            </div>
            {!acked && (
              <Button
                onClick={ack}
                disabled={submitting}
                data-testid="ack-rights-btn"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#0F8043] text-white h-10 px-5"
              >
                <ShieldCheck size={14} weight="fill" className="mr-1.5" />
                {submitting ? "Recording…" : "I've read my rights"}
              </Button>
            )}
          </div>
        )}

        {loading && <RowListSkeleton />}

        {!loading && (
          <ol className="space-y-3 mb-8" data-testid="rights-list">
            {(doc?.rights || []).map((r, idx) => (
              <li
                key={r.id}
                data-testid={`rights-item-${r.id}`}
                className="rounded-xl border border-[#E5E5E5] bg-white p-4 sm:p-5 flex gap-4"
              >
                <span className="flex-shrink-0 h-9 w-9 rounded-full bg-[#FBF7EC] text-[#8A6A00] flex items-center justify-center text-[14px] font-bold">
                  {idx + 1}
                </span>
                <div>
                  <h3 className="font-spectral text-lg text-ink tracking-tight mb-1">
                    {r.title}
                  </h3>
                  <p className="text-[13.5px] text-[#3F3F3F] leading-relaxed">
                    {r.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}

        {!loading && doc && (
          <section className="rounded-2xl bg-[#0E0E0E] text-white/85 p-5 sm:p-6 mb-8" data-testid="rights-disclaimer">
            <h2 className="font-spectral text-xl text-white tracking-tight mb-2 flex items-center gap-2">
              <Scales size={18} weight="duotone" className="text-gold" />
              Important legal notice
            </h2>
            <p className="text-[12.5px] leading-relaxed">{doc.legal_disclaimer}</p>
          </section>
        )}

        {!loading && doc && (
          <p className="text-[12px] text-[#6B6B6B]">
            Effective {doc.effective_date} · {total} rights ·{" "}
            <Link to="/seller-standards" className="underline hover:text-ink">Seller standards</Link>
          </p>
        )}
      </article>
    </Shell>
  );
}
